class RSSearchBox extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.clearButton = this.querySelector('.rs-search-clear');
    this.suggestionPanel = this.querySelector('.rs-search-popover');
    this.results = this.querySelector('[data-rs-search-results]');
    this.status = this.querySelector('[data-rs-search-status]');
    this.cache = new Map();
    this.activeIndex = -1;
    this.sequence = 0;
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length > 1) this.requestSuggestions();
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.clearButton.addEventListener('click', () => {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    });
    this.form.addEventListener('submit', () => this.close());
    this.addEventListener('focusout', () => {
      setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0);
    });
    this.outsideClick = (event) => {
      if (!this.contains(event.target)) {
        clearTimeout(this.timer);
        this.controller?.abort();
        this.sequence++;
        this.close();
      }
    };
    document.addEventListener('pointerdown', this.outsideClick);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
    document.removeEventListener('pointerdown', this.outsideClick);
    this.initialized = false;
  }

  onInput() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.sequence++;
    this.clearButton.hidden = !this.input.value;
    this.close();
    if (this.input.value.trim().length < 2 || !this.hasAttribute('data-predictive-enabled')) return;
    this.timer = setTimeout(() => this.requestSuggestions(), 250);
  }

  async requestSuggestions() {
    const query = this.input.value.trim();
    if (query.length < 2 || !this.hasAttribute('data-predictive-enabled')) return;
    this.controller?.abort();
    const request = ++this.sequence;
    this.controller = new AbortController();
    const key = query.toLocaleLowerCase();
    if (this.cache.has(key)) {
      this.render(this.cache.get(key));
      return;
    }
    this.status.textContent = this.dataset.loading;
    this.input.setAttribute('aria-busy', 'true');
    const url = new URL(this.dataset.predictiveUrl, window.location.origin);
    url.search = new URLSearchParams({q: query, 'resources[type]': 'product', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last', section_id: 'rs-search-suggestions'}).toString();
    try {
      const response = await fetch(url, {signal: this.controller.signal});
      if (!response.ok) throw new Error(String(response.status));
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-rs-suggestion-content]');
      if (!content) throw new Error('Missing search section');
      if (request !== this.sequence || query !== this.input.value.trim()) return;
      if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(key, content.innerHTML);
      this.render(content.innerHTML);
    } catch (error) {
      if (error.name !== 'AbortError' && request === this.sequence) {
        this.close();
        this.status.textContent = this.dataset.error;
      }
    } finally {
      if (request === this.sequence) this.input.removeAttribute('aria-busy');
    }
  }

  render(markup) {
    if (!this.contains(document.activeElement)) return;
    this.results.innerHTML = markup;
    const list = this.results.querySelector('[data-rs-suggestions]');
    list.id = `${this.input.id}-suggestions`;
    this.options = [...list.querySelectorAll('[role="option"]')];
    this.options.forEach((option, index) => {
      option.id = `${this.input.id}-option-${index}`;
      option.addEventListener('pointermove', () => this.select(index, false));
    });
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.suggestionPanel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.status.textContent = this.results.querySelector('[data-rs-suggestion-status]')?.textContent || '';
  }

  select(index, scroll = true) {
    this.options?.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    this.activeIndex = index;
    const option = this.options?.[index];
    if (!option) return;
    this.input.setAttribute('aria-activedescendant', option.id);
    if (scroll) option.scrollIntoView({block: 'nearest'});
  }

  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      clearTimeout(this.timer);
      this.controller?.abort();
      this.sequence++;
      this.close();
      return;
    }
    if (this.suggestionPanel.hidden || !this.options?.length) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const next = this.activeIndex < 0 ? (delta > 0 ? 0 : this.options.length - 1) : (this.activeIndex + delta + this.options.length) % this.options.length;
      this.select(next);
    }
    if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.options[this.activeIndex].querySelector('a').click();
    }
  }

  close() {
    this.suggestionPanel.hidden = true;
    this.activeIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.input.removeAttribute('aria-busy');
    this.status.textContent = '';
  }
}

if (!customElements.get('rs-search-box')) customElements.define('rs-search-box', RSSearchBox);
